/**
 * OrdersScreen — admin Orders / Transactions (new surface).
 *
 * The "own the money" screen: every paid order across events / appointments /
 * walk-ins, with KPIs (gross · refunded · net), status filter tabs, a
 * transactions table, and a detail drawer carrying line items, the provider
 * payment reference, a provider-hosted receipt link, and a Refund action.
 *
 * ABSOLUTE RULE 0: no seed / sample data here — every row + KPI comes from
 * `listOrders` (served by the real backend, or the __source-marked preview
 * seed in design-time). i18n: every string via t(key, fallback).
 */
import { useEffect, useState } from "react";
import { ChipToggleGroup, Btn, Card, Icon, Input, Pagination, Pill, T } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { EmptyState } from "../../shell/composites";
import { can } from "../../shell/access";
import { toast } from "../../shell/toast";
import ConfirmModal from "../../shell/ConfirmModal";
import { useT, t as i18nT } from "../../lib/i18n";
import { DataSourceStrip } from "../../shell/opSource";
import {
  listOrders, refundOrder, collectOrder, newIdempotencyKey, formatMinor,
  type Order, type OrderStatus, type OrdersKpis, type CollectMethod,
} from "./orders-api";

const ORDERS_OPS = ["listOrders", "getOrder", "refundOrder", "collectOrder"];

type Filter = "all" | "paid" | "reserved" | "refunded" | "pending";

const statusTone = (s: OrderStatus): "success" | "warn" | "neutral" | "danger" =>
  s === "paid" ? "success"
    : s === "reserved" || s === "refunded" || s === "partially_refunded" || s === "disputed" ? "warn"
    : s === "cancelled" || s === "failed" ? "danger"
    : "neutral";

export default function OrdersScreen() {
  const t = useT();
  const [filter, setFilter] = useState<Filter>("all");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState<Order[] | null>(null);
  const [kpis, setKpis] = useState<OrdersKpis | null>(null);
  const [err, setErr] = useState(false);
  const [selected, setSelected] = useState<Order | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);

  const PAGE_SIZE = 10;

  // Reset to page 1 whenever the filter or search changes. setPage(1) is a
  // no-op when already on page 1, so this never double-fetches.
  useEffect(() => { setPage(1); }, [filter, search]);

  useEffect(() => {
    let live = true;
    setRows(null); setErr(false);
    listOrders({ status: filter === "all" ? undefined : filter, search: search.trim() || undefined, page, pageSize: PAGE_SIZE })
      .then((resp) => { if (!live) return; setRows(resp.orders); setKpis(resp.kpis); setPages(resp.pages ?? 1); setTotal(resp.total ?? resp.orders.length); })
      .catch(() => { if (live) setErr(true); });
    return () => { live = false; };
  }, [filter, search, page, reloadTick]);

  const reload = () => setReloadTick((n) => n + 1);

  const statusLabel = (s: OrderStatus): string => ({
    paid: t("orders.status.paid", "Paid"),
    refunded: t("orders.status.refunded", "Refunded"),
    partially_refunded: t("orders.status.partiallyRefunded", "Partially refunded"),
    pending: t("orders.status.pending", "Pending"),
    reserved: t("orders.status.reserved", "Reserved · unpaid"),
    cancelled: t("orders.status.cancelled", "Canceled"),
    failed: t("orders.status.failed", "Failed"),
    disputed: t("orders.status.disputed", "Disputed"),
  }[s]);

  const sourceLabel = (s: Order["source"]): string => ({
    event: t("orders.source.event", "Event"),
    appointment: t("orders.source.appointment", "Appointment"),
    walkin: t("orders.source.walkin", "Walk-in"),
  }[s]);

  const tabs = ([
    ["all", t("orders.filter.all", "All")],
    ["paid", t("orders.filter.paid", "Paid")],
    ["reserved", t("orders.filter.reserved", "To collect")],
    ["refunded", t("orders.filter.refunded", "Refunded")],
    ["pending", t("orders.filter.pending", "Pending")],
  ] as [Filter, string][]).map(([id, label]) => ({ id, label, onClick: () => setFilter(id) }));

  return (
    <Screen rail="orders" tabs={tabs} activeTab={filter}>
      <PageHeader
        title={t("orders.title", "Orders")}
        subtitle={t("orders.subtitle", "Every paid transaction across events, appointments and walk-ins.")}
        actions={<Btn variant="secondary" size="sm" icon={<Icon.refresh size={14} />} onClick={reload}>{t("common.actions.refresh", "Refresh")}</Btn>}
      />
      <DataSourceStrip screen="orders" ops={ORDERS_OPS} />

      <div style={{ padding: "16px 24px 40px", display: "flex", flexDirection: "column", gap: 16 }}>
        {/* KPI row */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
          <KpiTile label={t("orders.kpi.gross", "Gross")} value={kpis ? formatMinor(kpis.grossMinor, kpis.currency) : "—"} />
          <KpiTile label={t("orders.kpi.refunded", "Refunded")} value={kpis ? formatMinor(kpis.refundedMinor, kpis.currency) : "—"} tone="warn" />
          <KpiTile label={t("orders.kpi.net", "Net")} value={kpis ? formatMinor(kpis.netMinor, kpis.currency) : "—"} tone="success" />
          <KpiTile label={t("orders.kpi.orders", "Orders")} value={kpis ? String(kpis.count) : "—"} />
          {kpis && kpis.reservedMinor ? (
            <KpiTile label={t("orders.kpi.toCollect", "To collect")} value={formatMinor(kpis.reservedMinor, kpis.currency)} tone="warn" />
          ) : null}
        </div>

        <div style={{ maxWidth: 320 }}>
          <Input value={search} placeholder={t("orders.searchPlaceholder", "Search by name, email or reference")} onChange={(e) => setSearch(e.target.value)} />
        </div>

        {/* States */}
        {err ? (
          <EmptyState
            variant="card"
            tone="danger"
            icon={<Icon.warn size={20} />}
            title={t("orders.loadErrorTitle", "Couldn't load transactions")}
            body={t("orders.loadError", "Something went wrong loading your orders. Please try again.")}
            action={<Btn variant="secondary" size="sm" icon={<Icon.refresh size={13} />} onClick={reload}>{t("common.actions.tryAgain", "Try again")}</Btn>}
          />
        ) : rows === null ? (
          <Card style={{ padding: 0, overflow: "hidden" }}>
            {[0, 1, 2, 3].map((i) => <div key={i} style={{ height: 52, borderBottom: `1px solid ${T.border}`, background: i % 2 ? T.white : T.n6, opacity: 0.6 }} />)}
          </Card>
        ) : rows.length === 0 ? (
          <EmptyState
            variant="card"
            icon={<Icon.list size={20} />}
            title={t("orders.emptyTitle", "No transactions yet")}
            body={t("orders.emptyBody", "Paid orders appear here as soon as your first attendee checks out.")}
          />
        ) : (
          <>
          <Card style={{ padding: 0, overflow: "hidden" }}>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5, minWidth: 720 }}>
                <thead>
                  <tr>
                    {[
                      t("orders.col.ref", "Reference"), t("orders.col.customer", "Customer"),
                      t("orders.col.source", "For"), t("orders.col.provider", "Provider"),
                      t("orders.col.amount", "Amount"), t("orders.col.status", "Status"), t("orders.col.date", "Date"),
                    ].map((h, i) => (
                      <th key={i} style={{ textAlign: i === 4 ? "right" : "left", padding: "11px 16px", background: T.n6, borderBottom: `1px solid ${T.border}`, fontSize: 11.5, textTransform: "uppercase", letterSpacing: 0.04, color: T.soft, fontWeight: 700, whiteSpace: "nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((o) => (
                    <tr key={o.id} onClick={() => setSelected(o)} style={{ cursor: "pointer", borderBottom: `1px solid ${T.border}` }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = T.n6)} onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                      <td style={{ padding: "12px 16px", fontFamily: T.fontMono, fontSize: 12.5, color: T.n1, whiteSpace: "nowrap" }}>{o.ref}</td>
                      <td style={{ padding: "12px 16px" }}>
                        <div style={{ fontWeight: 600, color: T.n1 }}>{o.customerName}</div>
                        {o.customerEmail ? <div style={{ fontSize: 12, color: T.soft }}>{o.customerEmail}</div> : null}
                      </td>
                      <td style={{ padding: "12px 16px", color: T.n2 }}>
                        <span style={{ fontSize: 11, color: T.soft }}>{sourceLabel(o.source)}</span>
                        <div style={{ color: T.n1 }}>{o.sourceLabel}</div>
                      </td>
                      <td style={{ padding: "12px 16px", color: T.n2, textTransform: "capitalize" }}>{o.provider ?? "—"}</td>
                      <td style={{ padding: "12px 16px", textAlign: "right", fontWeight: 700, color: T.n1, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>{formatMinor(o.totalMinor, o.currency)}</td>
                      <td style={{ padding: "12px 16px" }}><Pill tone={statusTone(o.status)}>{statusLabel(o.status)}</Pill></td>
                      <td style={{ padding: "12px 16px", color: T.soft, whiteSpace: "nowrap" }}>{formatDate(o.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Pagination */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <span style={{ fontSize: 12.5, color: T.soft }}>
              {t("orders.showing", "Showing")} {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} {t("common.of", "of")} {total}
            </span>
            {pages > 1 ? (
              <Pagination totalPages={pages} currentPage={page} onPageChange={setPage} size="sm" showStatus={false} />
            ) : null}
          </div>
          </>
        )}
      </div>

      {selected ? <OrderDrawer order={selected} onClose={() => setSelected(null)} onRefunded={() => { setSelected(null); reload(); }} t={t} statusLabel={statusLabel} /> : null}
    </Screen>
  );
}

function KpiTile({ label, value, tone }: { label: string; value: string; tone?: "success" | "warn" }) {
  const color = tone === "success" ? T.green1 : tone === "warn" ? T.orange1 : T.n1;
  return (
    <Card style={{ padding: "14px 16px" }}>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.05, color: T.soft }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, color, marginTop: 5, fontVariantNumeric: "tabular-nums" }}>{value}</div>
    </Card>
  );
}

function OrderDrawer({ order, onClose, onRefunded, t, statusLabel }: {
  order: Order; onClose: () => void; onRefunded: () => void;
  t: (k: string, f?: string) => string; statusLabel: (s: OrderStatus) => string;
}) {
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [collectOpen, setCollectOpen] = useState(false);
  const [method, setMethod] = useState<CollectMethod>("card_present");
  const refundable = order.status === "paid" && !!order.paymentId && can("billing.write");
  const collectible = order.status === "reserved" && can("billing.write");

  const doCollect = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await collectOrder({ orderId: order.id, method, idempotencyKey: newIdempotencyKey() });
      toast({ tone: "success", title: t("orders.collectOk", "Payment collected"), message: formatMinor(r.collectedMinor, order.currency) });
      setCollectOpen(false);
      onRefunded();
    } catch {
      toast({ tone: "error", title: t("orders.collectFail", "Couldn't collect"), message: t("orders.collectFailBody", "Nothing was charged — please try again.") });
    } finally {
      setBusy(false);
    }
  };

  const doRefund = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await refundOrder({ order, reason: "admin_refund", idempotencyKey: newIdempotencyKey() });
      toast({ tone: "success", title: t("orders.refundOk", "Refund issued"), message: formatMinor(r.amountMinor, order.currency) });
      setConfirmOpen(false);
      onRefunded();
    } catch {
      toast({ tone: "error", title: t("orders.refundFail", "Refund failed"), message: t("orders.refundFailBody", "Nothing was refunded — please try again.") });
    } finally {
      setBusy(false);
    }
  };

  const row = (label: string, value: React.ReactNode) => (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 13, padding: "7px 0" }}>
      <span style={{ color: T.soft }}>{label}</span>
      <span style={{ color: T.n1, fontWeight: 600, textAlign: "right" }}>{value}</span>
    </div>
  );

  return (
    <>
    <div onClick={onClose} role="dialog" aria-label={t("orders.detailTitle", "Order details")} style={{ position: "fixed", inset: 0, zIndex: 220, background: "rgba(20,16,46,0.45)", display: "flex", justifyContent: "flex-end" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: 440, maxWidth: "92vw", height: "100%", background: T.white, boxShadow: T.shadowLg, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div style={{ padding: "18px 22px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
          <div>
            <div style={{ fontFamily: T.fontMono, fontSize: 12.5, color: T.soft }}>{order.ref}</div>
            <h2 style={{ fontSize: 20, fontWeight: 800, color: T.n1, margin: "4px 0 0" }}>{formatMinor(order.totalMinor, order.currency)}</h2>
          </div>
          {/* ui-raw: filled-neutral 32px close (n6 fill, no border) — IconBtn draws a border and its fill comes from the state layer */}
          <button aria-label={t("common.actions.close", "Close")} onClick={onClose} style={{ width: 32, height: 32, borderRadius: 8, border: "none", background: T.n6, color: T.n2, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center" }}><Icon.x size={15} /></button>
        </div>

        <div style={{ flex: 1, overflow: "auto", padding: "16px 22px", display: "flex", flexDirection: "column", gap: 8 }}>
          {row(t("orders.detail.customer", "Customer"), <>{order.customerName}{order.customerEmail ? <div style={{ fontWeight: 400, color: T.soft, fontSize: 12 }}>{order.customerEmail}</div> : null}</>)}
          {row(t("orders.detail.status", "Status"), <Pill tone={statusTone(order.status)}>{statusLabel(order.status)}</Pill>)}
          {row(t("orders.detail.for", "For"), order.sourceLabel)}
          {row(t("orders.detail.provider", "Provider"), <span style={{ textTransform: "capitalize" }}>{order.provider ?? "—"}</span>)}
          {order.paymentRef ? row(t("orders.detail.paymentRef", "Payment reference"), <span style={{ fontFamily: T.fontMono, fontSize: 12 }}>{order.paymentRef}</span>) : null}
          {order.refundedMinor > 0 ? row(t("orders.detail.refunded", "Refunded"), formatMinor(order.refundedMinor, order.currency)) : null}

          {order.items && order.items.length ? (
            <div style={{ marginTop: 8, borderTop: `1px solid ${T.border}`, paddingTop: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.05, color: T.soft, marginBottom: 6 }}>{t("orders.detail.lineItems", "Line items")}</div>
              {order.items.map((it) => (
                <div key={it.id} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "5px 0", color: it.itemType === "discount" ? T.green1 : T.n1 }}>
                  <span>{it.description}</span>
                  <span style={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{formatMinor(it.amountMinor, order.currency)}</span>
                </div>
              ))}
            </div>
          ) : null}

          {order.receiptUrl ? (
            <a href={order.receiptUrl} target="_blank" rel="noreferrer" style={{ marginTop: 8, display: "inline-flex", alignItems: "center", gap: 7, fontSize: 13, fontWeight: 600, color: T.p1, textDecoration: "none" }}>
              <Icon.external size={13} /> {t("orders.viewReceipt", "View receipt")}
            </a>
          ) : null}
        </div>

        <div style={{ padding: "14px 22px", borderTop: `1px solid ${T.border}`, background: T.n6, display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Btn variant="secondary" onClick={onClose}>{t("common.actions.close", "Close")}</Btn>
          {collectible ? (
            <Btn disabled={busy} icon={<Icon.check size={14} />} onClick={() => setCollectOpen(true)}>
              {t("orders.collect", "Collect payment")}
            </Btn>
          ) : null}
          {refundable ? (
            <Btn variant="danger" disabled={busy} icon={<Icon.refresh size={14} />} onClick={() => setConfirmOpen(true)}>
              {t("orders.refund", "Refund")}
            </Btn>
          ) : null}
        </div>
      </div>
    </div>
    <ConfirmModal
      open={confirmOpen}
      danger
      busy={busy}
      title={t("orders.refundTitle", "Refund this order?")}
      message={i18nT("orders.refundConfirmMessage", "Refund {amount} to {name}. The buyer is refunded and the platform fee is reversed. This can't be undone.", { amount: formatMinor(order.totalMinor, order.currency), name: order.customerName })}
      confirmLabel={t("orders.refund", "Refund")}
      onConfirm={doRefund}
      onClose={() => { if (!busy) setConfirmOpen(false); }}
    />
    <ConfirmModal
      open={collectOpen}
      busy={busy}
      title={t("orders.collectTitle", "Collect payment on-site")}
      message={
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div>{t("orders.collectBody", "Record collection of")} <b>{formatMinor(order.totalMinor, order.currency)}</b> {t("orders.collectFrom", "from")} {order.customerName}.</div>
          <ChipToggleGroup
            ariaLabel={t("orders.collectMethod", "Method")}
            value={method}
            onChange={(v) => setMethod(v as CollectMethod)}
            options={([
              ["card_present", t("orders.method.cardPresent", "Card at door")],
              ["cash", t("orders.method.cash", "Cash")],
              ["card_on_file", t("orders.method.cardOnFile", "Card on file")],
              ["other", t("orders.method.other", "Other")],
            ] as [CollectMethod, string][]).map(([id, label]) => ({ value: id, label }))}
          />
        </div>
      }
      confirmLabel={t("orders.collect", "Collect payment")}
      onConfirm={doCollect}
      onClose={() => { if (!busy) setCollectOpen(false); }}
    />
    </>
  );
}

function formatDate(iso: string): string {
  try { return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", year: "numeric" }).format(new Date(iso)); }
  catch { return iso.slice(0, 10); }
}
